// import { Root as RootProfile } from 'services/get-profile'
import { useMetadataUser } from 'contexts/metadata-user-context'
import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { MdOutlineArrowBackIos, MdOutlineArrowForwardIos } from 'react-icons/md'
import { RxDoubleArrowLeft, RxDoubleArrowRight } from 'react-icons/rx'
import { getWalletCompanyTransactions } from 'services/get-wallet-company-transactions'
import convertNumbDote from 'utils/convertNumbDote'
import { Formatter } from 'utils/formatters'

import type { ActionType } from '..'

interface Transaction {
  amount: number
  created_at: string
  employee?: EmployeeInfo
}

interface EmployeeInfo {
  name: string
  email: string
}

interface LinksPagination {
  next: string | null
  prev: string | null
  first: string | null
  last: string | null
  total_pages: number
}

interface Transactions {
  data: Transaction[]
  total: number
  links: LinksPagination
}

interface selectedCardProps {
  selectedCard: ActionType
  setSelectedCard: React.Dispatch<ActionType | null>
  purchase_total?: number
}

export function CardWalletTransctionDetails({
  selectedCard,
  setSelectedCard,
}: selectedCardProps) {
  const { profile } = useMetadataUser()
  const [transactions, setTransactions] = useState<Transactions>({
    data: [],
    total: 0,
    links: {
      next: null,
      prev: null,
      first: null,
      last: null,
      total_pages: 1,
    },
  })

  const [loading, setLoading] = useState(true)
  const [currentOffset, setCurrentOffset] = useState(0)
  const limit = 10

  const fetchWalletCompanyTransactions = async (offset: number = 0) => {
    if (!profile) return

    const companyId = profile.company.id
    const transactionType = selectedCard === 'debits' ? 'REWARD' : 'PURCHASE'

    setLoading(true)
    try {
      const data = await getWalletCompanyTransactions({
        company_id: companyId,
        reference: transactionType,
        offset,
        limit,
      })
      setTransactions(data)
      setCurrentOffset(offset)
    } catch {
      toast.error(
        'Erro ao buscar dados da carteira da empresa. Tente novamente mais tarde.',
      )
      setTransactions({
        data: [],
        total: 0,
        links: {
          next: null,
          prev: null,
          first: null,
          last: null,
          total_pages: 0,
        },
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWalletCompanyTransactions(0)
  }, [profile, selectedCard])

  const goToNext = () => {
    if (transactions.links.next) {
      fetchWalletCompanyTransactions(currentOffset + limit)
    }
  }

  const goToPrev = () => {
    if (transactions.links.prev) {
      fetchWalletCompanyTransactions(Math.max(0, currentOffset - limit))
    }
  }

  const goToFirst = () => {
    fetchWalletCompanyTransactions(0)
  }

  const goToLast = () => {
    if (transactions.links.last) {
      const lastOffset = (transactions.links.total_pages - 1) * limit
      fetchWalletCompanyTransactions(lastOffset)
    }
  }

  const currentPage = Math.floor(currentOffset / limit) + 1

  const currentPageTotal = transactions.data.reduce(
    (acc, curr) => acc + curr.amount,
    0,
  )

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
          {selectedCard === 'credits'
            ? 'Detalhamento de Créditos'
            : 'Detalhamento de Débitos'}
        </h2>
        <button
          type="button"
          onClick={() => setSelectedCard(null)}
          className="rounded-lg px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-700 dark:hover:text-slate-200"
        >
          Voltar
        </button>
      </div>

      <div className="grid gap-6">
        <div className="space-y-4">
          <div className="rounded-lg bg-slate-50 p-4 dark:bg-slate-700/50">
            <span className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400">
              {selectedCard === 'credits'
                ? 'Total de Créditos'
                : 'Total de Débitos'}
            </span>
            <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">
              {loading ? (
                <span className="text-slate-400">Carregando...</span>
              ) : (
                convertNumbDote(
                  transactions.data.reduce((acc, curr) => acc + curr.amount, 0),
                )
              )}
            </p>
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              {`${transactions.data.length} ${transactions.data.length > 1 ? 'Transações' : 'Transação'}`}
            </p>
          </div>

          <div className="rounded-lg bg-white p-4 shadow-sm dark:bg-slate-800">
            <h3 className="mb-4 font-semibold text-slate-900 dark:text-white">
              {selectedCard === 'credits'
                ? 'Histórico de Créditos'
                : 'Histórico de Débitos'}
            </h3>
            <div className="space-y-2">
              {loading && (
                <div className="flex justify-center py-4">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-600" />
                </div>
              )}

              {!loading && transactions.data.length === 0 && (
                <p className="text-center text-sm text-slate-500 dark:text-slate-400">
                  Nenhuma transação encontrada
                </p>
              )}

              {!loading &&
                transactions.data.length > 0 &&
                transactions.data.map((transaction, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between rounded-lg border border-slate-100 p-3 dark:border-slate-700"
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900 dark:text-white">
                        Transação #{index + 1}
                      </p>
                      {transaction.employee && (
                        <div className="mt-1 space-y-1">
                          <p className="text-xs text-slate-600 dark:text-slate-400">
                            <span className="font-semibold">Funcionário:</span>{' '}
                            {transaction.employee.name}
                          </p>
                          <p className="text-xs text-slate-600 dark:text-slate-400">
                            <span className="font-semibold">Email:</span>{' '}
                            {transaction.employee.email}
                          </p>
                        </div>
                      )}
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        {
                          Formatter.formatterDateAndHour(transaction.created_at)
                            .hourAndDateFormateds
                        }
                      </p>
                    </div>
                    <span className="text-sm font-semibold text-slate-900 dark:text-white">
                      {convertNumbDote(currentPageTotal)}
                    </span>
                  </div>
                ))}

              {transactions.links.total_pages > 1 && (
                <div className="mt-6 flex items-center justify-center gap-2 pt-4 border-t border-slate-200 dark:border-slate-700">
                  <button
                    onClick={goToFirst}
                    disabled={!transactions.links.first || currentPage === 1}
                    className="rounded-md px-3 py-2 text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors dark:bg-slate-600 dark:hover:bg-slate-500 dark:disabled:bg-slate-700"
                  >
                    <RxDoubleArrowLeft />
                  </button>

                  <button
                    onClick={goToPrev}
                    disabled={!transactions.links.prev}
                    className="rounded-md flex items-center justify-items-center py-2 px-10 text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors dark:bg-slate-600 dark:hover:bg-slate-500 dark:disabled:bg-slate-700"
                  >
                    <MdOutlineArrowBackIos className="mr-2" size={14} />
                    Anterior
                  </button>

                  <span className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                    Página {currentPage} de {transactions.links.total_pages}
                  </span>

                  <button
                    onClick={goToNext}
                    disabled={!transactions.links.next}
                    className="rounded-md flex items-center justify-items-center py-2 px-10 text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors dark:bg-slate-600 dark:hover:bg-slate-500 dark:disabled:bg-slate-700"
                  >
                    Próxima
                    <MdOutlineArrowForwardIos className="ml-1" size={14} />
                  </button>

                  <button
                    onClick={goToLast}
                    disabled={
                      !transactions.links.last ||
                      currentPage === transactions.links.total_pages
                    }
                    className="rounded-md px-3 py-2 text-sm font-medium text-white bg-slate-600 hover:bg-slate-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors dark:bg-slate-600 dark:hover:bg-slate-500 dark:disabled:bg-slate-700"
                  >
                    <RxDoubleArrowRight />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
